"use client";

import {
    create,
    useStore,
    type StateCreator,
    type StoreApi,
} from "zustand";
import { createStore } from "zustand/vanilla";
import { temporal } from "zundo";
import { createContext, useContext } from "react";
import { v4 as uuidv4 } from "uuid";
import type { Editor } from "@tiptap/react";
import {
    LomdaV3Asset,
    LomdaV3Block,
    LomdaV3BlockData,
    LomdaV3BlockPatch,
    LomdaV3BlockType,
    LomdaV3Content,
    LomdaV3Metadata,
    LomdaV3Page,
    LomdaV3PlayerSettings,
    MediaCell,
    PageBackground,
    TextBackground,
    TextVariant,
} from "@/lib/utils/lomdaV3/types";
import {
    BlockThemeOverride,
    LomdaTheme,
    LomdaV3FontWeights,
    LomdaV3LineHeights,
} from "@/lib/utils/lomdaV3/types";
import {
    BlockList,
    EditorMode,
    LomdaV3EditorInit,
    sameBlockList,
} from "../lib/types";
import {
    DEFAULT_BLOCK_GAP,
    DEFAULT_GRID_GAP,
    DEFAULT_INNER_GAP,
    DEFAULT_THEME,
} from "../lib/theme";
import { createEmptyPage } from "../lib/constants";
import { canNestIn, convertTextData } from "../lib/blockRegistry";
import {
    clearOwnFonts,
    type OwnFonts,
    type ThemeFontKey,
} from "../lib/ownFonts";
import { withoutMedia, type MediaKey } from "../lib/mediaAssets";
import { MAX_CUSTOM_ICONS } from "../lib/uploadLimits";
import { mergeUnique, type MergeResult } from "@/lib/utils/mergeUnique";

type BlockOfType<K extends LomdaV3BlockType> = Extract<LomdaV3Block, { type: K }>;

export interface LomdaV3EditorState {
    // identity
    userId: string;
    lomdaId: string;
    lomdaName: string;

    // data
    content: LomdaV3Content;
    metadata: Omit<LomdaV3Metadata, "assets">;
    // `metadata.assets`, held apart so undo never drops an upload from the
    // library. Autosave merges it back into the saved metadata.
    assets: LomdaV3Asset[];

    // ui
    mode: EditorMode;
    selectedPageId: string | null;
    selectedBlockId: string | null;
    // A block to scroll into view once it renders (see `revealBlock`).
    revealBlockId: string | null;
    // The open tab / step of each Tabs / Steps block, by container id. Editor
    // view state: not saved and not in undo.
    openSlots: Record<string, string>;
    // The column last clicked inside; where Add puts a block when its Columns
    // block is selected. Any click on a page block clears it first.
    activeSlot: BlockList;
    // A copied block, held so it can be pasted onto any page. Transient UI state,
    // never persisted into the saved lomda.
    clipboardBlock: CopiedBlock | null;
    // The block whose editor modal/sheet is currently open.
    editingBlockId: string | null;
    // The rich-text editor that currently owns the contextual topbar (last focused).
    activeEditor: Editor | null;
    // The block id that owns `activeEditor` (so block-level controls target it).
    activeBlockId: string | null;
    // The formatting-group the active editor belongs to (e.g. all step titles).
    // Tag changes from the toolbar apply to every editor in the same group.
    activeGroupKey: string | null;
    // The focused sub-item (e.g. knowledge-check question) whose 2nd-tier
    // settings the topbar should show. Cleared when selection changes.
    focusedQuestionId: string | null;
    // Per-html-block "show preview vs code". Transient VIEW state — deliberately
    // not in block.data, so it never persists into the saved lomda.
    htmlPreview: Record<string, boolean>;

    // save status
    isSaving: boolean;
    saveError: string | null;
    lastSaved: Date | null;

    initialized: boolean;

    // lifecycle
    initialize: (data: LomdaV3EditorInit) => void;

    // navigation
    setMode: (mode: EditorMode) => void;
    setSelectedPageId: (pageId: string | null) => void;
    setSelectedBlockId: (blockId: string | null) => void;
    /** Go to a block from outside the canvas: switch to its page, open the tab
     *  or step that holds it, scroll it into view and select it. */
    revealBlock: (blockId: string, pageId?: string) => void;
    clearReveal: () => void;
    setOpenSlot: (containerId: string, slotId: string) => void;
    setActiveSlot: (slot: BlockList) => void;
    /** Select a block AND drop any active rich-text editor → show its settings. */
    showBlockSettings: (blockId: string) => void;
    openBlockEditor: (blockId: string) => void;
    closeBlockEditor: () => void;

    /** Focus a sub-item (knowledge-check question) for its 2nd-tier settings. */
    setFocusedQuestion: (questionId: string | null) => void;

    /** Toggle an html block between code and rendered preview. */
    setHtmlPreview: (blockId: string, preview: boolean) => void;

    // contextual topbar editor
    setActiveEditor: (
        editor: Editor,
        blockId?: string | null,
        groupKey?: string | null
    ) => void;
    releaseActiveEditor: (editor: Editor) => void;
    /** Drop the active rich-text editor (e.g. clicking a non-text control). */
    clearActiveEditor: () => void;

    // pages
    addPage: () => void;
    deletePage: (pageId: string) => void;
    duplicatePage: (pageId: string) => void;
    reorderPages: (fromIndex: number, toIndex: number) => void;
    setPageTitle: (pageId: string, title: string) => void;
    setPageBackground: (
        pageId: string,
        background: PageBackground | undefined
    ) => void;
    clearPageBackgrounds: (pageIds: readonly string[]) => void;
    getSelectedPage: () => LomdaV3Page | null;

    // blocks (operate on the selected page)
    addBlock: (block: LomdaV3Block, atIndex?: number) => void;
    /** Add / a palette click: insert where `insertPoint` says, then reveal. */
    addBlockAtInsertPoint: (block: LomdaV3Block) => void;
    addBlockToSlot: (
        containerId: string,
        slotId: string,
        block: LomdaV3Block,
        atIndex?: number
    ) => void;
    updateBlock: (blockId: string, patch: LomdaV3BlockPatch) => void;
    /** Patch a block from its latest data, on whichever page holds it, so a
     *  result that lands later (an upload, a pick) never writes back a stale
     *  copy (U1b). A `null` patch writes nothing. */
    patchBlockData: <K extends LomdaV3BlockType>(
        blockId: string,
        type: K,
        patch: (
            data: BlockOfType<K>["data"]
        ) => Partial<BlockOfType<K>["data"]> | null
    ) => void;
    updateMediaCell: (
        blockId: string,
        index: number,
        patch: Partial<MediaCell> | ((cell: MediaCell) => Partial<MediaCell>)
    ) => void;
    replaceBlockData: (blockId: string, data: LomdaV3BlockData) => void;
    /** The text block's section background — a sibling of `data`. Merged into
     *  the latest background; `undefined` clears it. */
    patchBlockBackground: (
        blockId: string,
        patch: Partial<TextBackground> | undefined
    ) => void;
    changeTextLayout: (blockId: string, variant: TextVariant) => void;
    deleteBlock: (blockId: string) => void;
    duplicateBlock: (blockId: string) => void;
    /** Move a block off the selected page onto another page (appended).
     *  False when nothing moved. */
    moveBlockToPage: (blockId: string, targetPageId: string) => boolean;
    /** Copy a block off the selected page to the end of another page, like
     *  duplicate (new ids, settings kept). Returns the copy's id. */
    copyBlockToPage: (blockId: string, targetPageId: string) => string | null;
    /** Copy a block (from the selected page) into the clipboard. */
    copyBlock: (blockId: string) => void;
    /** Paste the clipboard block onto the selected page (fresh ids). */
    pasteBlock: () => void;
    /** Move a block anywhere on the selected page: no `containerId` = the page
     *  itself. `index` is the spot as it looks now, the block still in place. */
    moveBlock: (
        blockId: string,
        to: { containerId?: string; slotId?: string; index: number }
    ) => void;

    // misc
    setLomdaName: (name: string) => void;
    setLanguage: (language: string) => void;
    /** Off removes the field, so a lomda switched back saves as it was. */
    setPlayerSetting: (key: keyof LomdaV3PlayerSettings, on: boolean) => void;
    setTheme: (theme: LomdaTheme) => void;
    /** Reset the theme. `brandIcons` (the kit's current icon set) restores the
     *  icon list exactly — kit icons deleted here come back, lomda uploads go.
     *  `switchOwn` (the prompt's Switch) drops those texts' own fonts too. */
    resetTheme: (brandIcons?: string[], switchOwn?: OwnFonts | null) => void;
    setThemeVar: <K extends keyof LomdaTheme>(
        key: K,
        value: LomdaTheme[K]
    ) => void;
    /** Change the titles or body font. `switchOwn` (the prompt's scan, on
     *  Switch) also drops those texts' own fonts — in the same step, so one
     *  undo restores both. */
    setThemeFont: (
        key: ThemeFontKey,
        family: string,
        switchOwn: OwnFonts | null
    ) => void;
    /** Append an uploaded custom font to the lomda's font list (dedup by url). */
    addCustomFont: (font: { family: string; url: string }) => void;
    /** Append uploaded icons to the lomda's icon list (dedup by url, at most
     *  MAX_CUSTOM_ICONS) in one undo step, so every icon picker offers them. */
    addCustomIcons: (urls: string[]) => MergeResult;
    /** Delete a file from the library and clear it from every block, in one
     *  undo step. The upload record stays; `metadata.deletedAssets` hides it. */
    deleteMedia: (file: MediaKey) => void;
    /** Add an upload to the media library (dedup by url). */
    recordAsset: (asset: LomdaV3Asset) => boolean;
    setBlockTheme: (blockId: string, override: BlockThemeOverride) => void;
    clearBlockTheme: (blockId: string) => void;
    setBlockGap: (gap: number) => void;
    setFontWeight: (
        preset: keyof LomdaV3FontWeights,
        weight: number
    ) => void;
    setLineHeight: (
        preset: keyof LomdaV3LineHeights,
        lineHeight: number
    ) => void;
    setGridGap: (gap: number) => void;
    setInnerGap: (gap: number) => void;
    setBlockGridGap: (blockId: string, gap: number) => void;
    setBlockWidth: (blockId: string, width: number) => void;
    setSaveStatus: (status: {
        isSaving?: boolean;
        saveError?: string | null;
        lastSaved?: Date | null;
    }) => void;
}

const withPageMeta = (
    content: LomdaV3Content,
    metadata: LomdaV3EditorState["metadata"],
    pages: LomdaV3Page[]
) => ({
    content: { ...content, pages },
    metadata: { ...metadata, numOfPages: pages.length },
});

// Replace the selected page's blocks via an updater, returning new content.
const withSelectedPageBlocks = (
    content: LomdaV3Content,
    selectedPageId: string | null,
    updater: (blocks: LomdaV3Block[]) => LomdaV3Block[]
): LomdaV3Content => ({
    ...content,
    pages: content.pages.map((p) =>
        p.id === selectedPageId ? { ...p, blocks: updater(p.blocks) } : p
    ),
});

// Blocks can nest inside container blocks (columns / tabs / steps), each of
// which holds child blocks in one or more "slots". The helpers below walk the
// whole tree (page blocks + every container slot) by id.
const isContainer = (b: LomdaV3Block) =>
    b.type === "columns" || b.type === "tabs" || b.type === "steps";

const isListOnlySteps = (b: LomdaV3Block | null) =>
    b?.type === "steps" && b.data.display === "list";

// The child-block slots of a container, as { id, blocks } (read-only view).
const blockSlots = (
    b: LomdaV3Block
): { id: string; blocks: LomdaV3Block[] }[] | null => {
    if (b.type === "columns") return b.data.columns;
    if (b.type === "tabs")
        return b.data.items.map((it) => ({ id: it.id, blocks: it.blocks ?? [] }));
    if (b.type === "steps")
        return b.data.items.map((it) => ({ id: it.id, blocks: it.blocks ?? [] }));
    return null;
};

// Rebuild a container, mapping each slot's blocks via `mapBlocks(blocks, slotId)`.
const withSlotBlocks = (
    b: LomdaV3Block,
    mapBlocks: (blocks: LomdaV3Block[], slotId: string) => LomdaV3Block[]
): LomdaV3Block => {
    if (b.type === "columns")
        return {
            ...b,
            data: {
                ...b.data,
                columns: b.data.columns.map((c) => ({
                    ...c,
                    blocks: mapBlocks(c.blocks, c.id),
                })),
            },
        };
    if (b.type === "tabs")
        return {
            ...b,
            data: {
                ...b.data,
                items: b.data.items.map((it) => ({
                    ...it,
                    blocks: mapBlocks(it.blocks ?? [], it.id),
                })),
            },
        };
    if (b.type === "steps")
        return {
            ...b,
            data: {
                ...b.data,
                items: b.data.items.map((it) => ({
                    ...it,
                    blocks: mapBlocks(it.blocks ?? [], it.id),
                })),
            },
        };
    return b;
};

const mapBlockTree = (
    blocks: LomdaV3Block[],
    fn: (b: LomdaV3Block) => LomdaV3Block
): LomdaV3Block[] =>
    blocks.map((b) => {
        const mapped = fn(b);
        return isContainer(mapped)
            ? withSlotBlocks(mapped, (arr) => mapBlockTree(arr, fn))
            : mapped;
    });

/** `updater` run on the page that holds block `id` (the selected page first),
 *  so a result that lands after a page switch still reaches its block. Null
 *  if no page has it. */
const withBlockPage = (
    state: Pick<LomdaV3EditorState, "content" | "selectedPageId">,
    id: string,
    updater: (blocks: LomdaV3Block[]) => LomdaV3Block[]
): LomdaV3Content | null => {
    const { pages } = state.content;
    const page =
        pages.find(
            (p) =>
                p.id === state.selectedPageId && findBlockDeep(p.blocks, id)
        ) ?? pages.find((p) => findBlockDeep(p.blocks, id));
    return page ? withSelectedPageBlocks(state.content, page.id, updater) : null;
};

const removeBlockTree = (
    blocks: LomdaV3Block[],
    id: string
): LomdaV3Block[] =>
    blocks
        .filter((b) => b.id !== id)
        .map((b) =>
            isContainer(b)
                ? withSlotBlocks(b, (arr) => removeBlockTree(arr, id))
                : b
        );

const locateBlock = (
    blocks: LomdaV3Block[],
    id: string,
    list: BlockList = null
): { block: LomdaV3Block; list: BlockList; index: number } | null => {
    for (let index = 0; index < blocks.length; index++) {
        const b = blocks[index];
        if (b.id === id) return { block: b, list, index };
        for (const s of blockSlots(b) ?? []) {
            const found = locateBlock(s.blocks, id, {
                containerId: b.id,
                slotId: s.id,
            });
            if (found) return found;
        }
    }
    return null;
};

/** The slots that wrap block `id`, outermost first; null if it isn't there. */
const slotPathTo = (
    blocks: LomdaV3Block[],
    id: string
): { containerId: string; slotId: string }[] | null => {
    for (const b of blocks) {
        if (b.id === id) return [];
        for (const s of blockSlots(b) ?? []) {
            const rest = slotPathTo(s.blocks, id);
            if (rest) return [{ containerId: b.id, slotId: s.id }, ...rest];
        }
    }
    return null;
};

/** Select block `id`, open every tab / step around it and ask it to scroll
 *  into view. Inside a list-only steps block, the steps block stands in. */
const revealState = (
    state: Pick<LomdaV3EditorState, "openSlots">,
    pageBlocks: LomdaV3Block[],
    id: string
): Partial<LomdaV3EditorState> => {
    const path = slotPathTo(pageBlocks, id) ?? [];
    const hidden = path.findIndex((slot) =>
        isListOnlySteps(findBlockDeep(pageBlocks, slot.containerId))
    );
    const target = hidden < 0 ? id : path[hidden].containerId;
    const openSlots = { ...state.openSlots };
    for (const slot of hidden < 0 ? path : path.slice(0, hidden))
        openSlots[slot.containerId] = slot.slotId;
    return {
        selectedBlockId: target,
        revealBlockId: target,
        openSlots,
        activeEditor: null,
        activeGroupKey: null,
        focusedQuestionId: null,
    };
};

/** `revealState` for a block just added to the selected page. */
const revealNewBlock = (
    state: Pick<LomdaV3EditorState, "openSlots" | "selectedPageId">,
    content: LomdaV3Content,
    id: string
) =>
    revealState(
        state,
        content.pages.find((p) => p.id === state.selectedPageId)?.blocks ?? [],
        id
    );

/**
 * Where Add and a palette click put a new block:
 * - a container (Columns, Tabs, Steps) selected → the end of its slot: for
 *   Columns the column last clicked inside, else column 1; for Tabs / Steps the
 *   open one;
 * - any other block selected → right after it, in its own list;
 * - nothing selected → the end of the page.
 * Where the nesting rules refuse the block, it goes right after the
 * container instead, one level up at a time.
 */
const insertPoint = (
    pageBlocks: LomdaV3Block[],
    state: Pick<
        LomdaV3EditorState,
        "selectedBlockId" | "activeSlot" | "openSlots"
    >,
    type: LomdaV3BlockType
): { list: BlockList; index: number } => {
    const selected = state.selectedBlockId
        ? locateBlock(pageBlocks, state.selectedBlockId)
        : null;
    if (!selected) return { list: null, index: pageBlocks.length };

    const container = selected.block;
    const slots = blockSlots(container) ?? [];
    const wanted =
        container.type === "columns"
            ? state.activeSlot?.containerId === container.id
                ? state.activeSlot.slotId
                : undefined
            : state.openSlots[container.id];
    const slot = slots.find((s) => s.id === wanted) ?? slots[0];

    let at: { list: BlockList; index: number } = slot
        ? {
              list: { containerId: container.id, slotId: slot.id },
              index: slot.blocks.length,
          }
        : { list: selected.list, index: selected.index + 1 };
    while (at.list && !canMoveBlock(pageBlocks, { type }, at.list)) {
        const parent = locateBlock(pageBlocks, at.list.containerId);
        if (!parent) return { list: null, index: pageBlocks.length };
        at = { list: parent.list, index: parent.index + 1 };
    }
    return at;
};

const insertBlock = (
    blocks: LomdaV3Block[],
    to: BlockList,
    index: number | undefined,
    block: LomdaV3Block
): LomdaV3Block[] => {
    const insert = (arr: LomdaV3Block[]) => {
        const next = [...arr];
        next.splice(index ?? next.length, 0, block);
        return next;
    };
    if (!to) return insert(blocks);
    return mapBlockTree(blocks, (b) =>
        b.id === to.containerId
            ? withSlotBlocks(b, (arr, sid) => (sid === to.slotId ? insert(arr) : arr))
            : b
    );
};

/** The one nesting rule for moves and drops. Palette cards pass no `id`. */
export const canMoveBlock = (
    pageBlocks: LomdaV3Block[],
    moving: { id?: string; type: LomdaV3BlockType },
    to: BlockList
): boolean => {
    if (!to) return true;
    const container = findBlockDeep(pageBlocks, to.containerId);
    if (!container || !blockSlots(container)?.some((s) => s.id === to.slotId))
        return false;
    if (
        isListOnlySteps(container) ||
        slotPathTo(pageBlocks, to.containerId)?.some((s) =>
            isListOnlySteps(findBlockDeep(pageBlocks, s.containerId))
        )
    )
        return false;
    if (!canNestIn(moving.type, container.type)) return false;
    // A container can't go into itself or anything inside it.
    const self = moving.id ? findBlockDeep(pageBlocks, moving.id) : null;
    return !self || !findBlockDeep([self], to.containerId);
};

// Per-block settings that live outside the block, keyed by its id.
type BlockSettings = Pick<
    LomdaV3Content,
    "blockWidths" | "blockThemes" | "blockGridGaps"
>;

type CopiedBlock = { block: LomdaV3Block; settings: BlockSettings };

const blockSettings = (content: LomdaV3Content): BlockSettings => ({
    blockWidths: content.blockWidths,
    blockThemes: content.blockThemes,
    blockGridGaps: content.blockGridGaps,
});

const copyEntries = <T>(
    into: Record<string, T> | undefined,
    from: Record<string, T> | undefined,
    ids: Map<string, string>
): Record<string, T> | undefined => {
    if (!from) return into;
    const copied = Array.from(ids).flatMap(([oldId, newId]) =>
        from[oldId] === undefined ? [] : [[newId, from[oldId]] as const]
    );
    return copied.length ? { ...into, ...Object.fromEntries(copied) } : into;
};

const withCopiedSettings = (
    content: LomdaV3Content,
    from: BlockSettings,
    ids: Map<string, string>
): LomdaV3Content => ({
    ...content,
    blockWidths: copyEntries(content.blockWidths, from.blockWidths, ids),
    blockThemes: copyEntries(content.blockThemes, from.blockThemes, ids),
    blockGridGaps: copyEntries(content.blockGridGaps, from.blockGridGaps, ids),
});

const treeIds = (blocks: LomdaV3Block[]): string[] =>
    blocks.flatMap((b) => [
        b.id,
        ...(blockSlots(b) ?? []).flatMap((s) => treeIds(s.blocks)),
    ]);

// Builds a new map, so a clipboard copy holding the old one keeps its entries.
const dropEntries = <T>(
    map: Record<string, T> | undefined,
    ids: string[]
): Record<string, T> | undefined => {
    if (!map || !ids.some((id) => map[id] !== undefined)) return map;
    const next = { ...map };
    for (const id of ids) delete next[id];
    return next;
};

const withoutSettings = (
    content: LomdaV3Content,
    ids: string[]
): LomdaV3Content => ({
    ...content,
    blockWidths: dropEntries(content.blockWidths, ids),
    blockThemes: dropEntries(content.blockThemes, ids),
    blockGridGaps: dropEntries(content.blockGridGaps, ids),
});

// Only ids that are now nowhere in the lomda lose their settings, so a block
// that moved to another slot or page keeps its own.
const withoutGoneSettings = (
    content: LomdaV3Content,
    ids: string[]
): LomdaV3Content => {
    if (!ids.length) return content;
    const alive = new Set(content.pages.flatMap((p) => treeIds(p.blocks)));
    const gone = ids.filter((id) => !alive.has(id));
    return gone.length ? withoutSettings(content, gone) : content;
};

const findInLomda = (content: LomdaV3Content, id: string) => {
    for (const p of content.pages) {
        const found = findBlockDeep(p.blocks, id);
        if (found) return found;
    }
    return null;
};

/** After block `id` is rebuilt: blocks that were inside it and are now nowhere
 *  in the lomda (a deleted tab or step) lose their settings. */
const withoutDroppedSettings = (
    state: Pick<LomdaV3EditorState, "content">,
    after: LomdaV3Content,
    id: string
): LomdaV3Content => {
    const before = findInLomda(state.content, id);
    if (!before || !isContainer(before)) return after;
    const now = findInLomda(after, id);
    const kept = new Set(now ? treeIds([now]) : []);
    const dropped = treeIds([before]).filter((x) => !kept.has(x));
    return withoutGoneSettings(after, dropped);
};

/** Clones get new ids (inside too); `withSettings` then gives them the
 *  width, colours and gap their originals have in `settings`. */
const cloneWithSettings = (settings: BlockSettings) => {
    const ids = new Map<string, string>();
    return {
        clone: (block: LomdaV3Block) => cloneBlock(block, ids),
        withSettings: (content: LomdaV3Content) =>
            withCopiedSettings(content, settings, ids),
    };
};

// `ids` collects old id → new id for the block and everything inside it.
const cloneBlock = (
    b: LomdaV3Block,
    ids: Map<string, string>
): LomdaV3Block => {
    const cloned = { ...b, id: uuidv4() } as LomdaV3Block;
    ids.set(b.id, cloned.id);
    const cloneAll = (blocks: LomdaV3Block[]) =>
        blocks.map((child) => cloneBlock(child, ids));
    if (cloned.type === "columns")
        return {
            ...cloned,
            data: {
                ...cloned.data,
                columns: cloned.data.columns.map((c) => ({
                    ...c,
                    id: uuidv4(),
                    blocks: cloneAll(c.blocks),
                })),
            },
        };
    if (cloned.type === "tabs")
        return {
            ...cloned,
            data: {
                ...cloned.data,
                items: cloned.data.items.map((it) => ({
                    ...it,
                    id: uuidv4(),
                    blocks: cloneAll(it.blocks ?? []),
                })),
            },
        };
    if (cloned.type === "steps")
        return {
            ...cloned,
            data: {
                ...cloned.data,
                items: cloned.data.items.map((it) => ({
                    ...it,
                    id: uuidv4(),
                    blocks: cloneAll(it.blocks ?? []),
                })),
            },
        };
    return cloned;
};

const duplicateInTree = (
    blocks: LomdaV3Block[],
    id: string,
    clone: (block: LomdaV3Block) => LomdaV3Block
): { blocks: LomdaV3Block[]; newId: string | null } => {
    const index = blocks.findIndex((b) => b.id === id);
    if (index !== -1) {
        const copy = clone(blocks[index]);
        return {
            blocks: [
                ...blocks.slice(0, index + 1),
                copy,
                ...blocks.slice(index + 1),
            ],
            newId: copy.id,
        };
    }
    let newId: string | null = null;
    const next = blocks.map((b) => {
        if (newId || !isContainer(b)) return b;
        return withSlotBlocks(b, (arr) => {
            if (newId) return arr;
            const res = duplicateInTree(arr, id, clone);
            if (res.newId) {
                newId = res.newId;
                return res.blocks;
            }
            return arr;
        });
    });
    return { blocks: next, newId };
};

export const findBlockDeep = (
    blocks: LomdaV3Block[],
    id: string | null
): LomdaV3Block | null => {
    if (!id) return null;
    for (const b of blocks) {
        if (b.id === id) return b;
        const slots = blockSlots(b);
        if (slots) {
            for (const s of slots) {
                const found = findBlockDeep(s.blocks, id);
                if (found) return found;
            }
        }
    }
    return null;
};

/** Collapse a rapid burst of edits (e.g. dragging a colour picker) into one
 * undo step by recording only the FIRST change after a quiet gap and skipping
 * the rest of the burst. The gap is measured from the last call (not the last
 * recorded step), so deliberate actions spaced apart — a page switch or colour
 * change right after another edit — are each still recorded as their own step. */
function burstCollapse<T>(fn: T, ms: number): T {
    let lastCall = 0;
    const wrapped = (...args: unknown[]) => {
        const now = Date.now();
        const startsBurst = now - lastCall >= ms;
        lastCall = now;
        if (startsBurst) (fn as (...a: unknown[]) => void)(...args);
    };
    return wrapped as T;
}

/**
 * Clear icon references the lomda no longer offers — a deleted upload must not
 * linger on a card or tab as a broken image. Walks nested blocks too (a card
 * can live inside tabs / steps / columns).
 */
function clearIcons(
    blocks: LomdaV3Block[],
    isGone: (icon: string) => boolean
): LomdaV3Block[] {
    return blocks.map((b) => {
        if (b.type === "flip-cards") {
            return {
                ...b,
                data: {
                    ...b.data,
                    cards: b.data.cards.map((c) =>
                        c.icon && isGone(c.icon) ? { ...c, icon: "" } : c
                    ),
                },
            };
        }
        if (b.type === "tabs") {
            return {
                ...b,
                data: {
                    ...b.data,
                    items: b.data.items.map((i) => ({
                        ...i,
                        icon: i.icon && isGone(i.icon) ? "" : i.icon,
                        blocks: clearIcons(i.blocks ?? [], isGone),
                    })),
                },
            };
        }
        if (b.type === "steps") {
            return {
                ...b,
                data: {
                    ...b.data,
                    items: b.data.items.map((i) => ({
                        ...i,
                        blocks: clearIcons(i.blocks ?? [], isGone),
                    })),
                },
            };
        }
        if (b.type === "columns") {
            return {
                ...b,
                data: {
                    ...b.data,
                    columns: b.data.columns.map((c) => ({
                        ...c,
                        blocks: clearIcons(c.blocks ?? [], isGone),
                    })),
                },
            };
        }
        return b;
    });
}

/**
 * Icons uploaded INSIDE this lomda (picker / settings panel) go to a
 * `lomda-v3/<id>/icons` path, while the brand kit's own icons live under
 * `brand-kits/icons` — the path is what tells them apart after a reload.
 */
const isLomdaUpload = (icon: string) => icon.includes("/lomda-v3/");

/** Uploaded icons are URLs; the built-in ones are plain keys and always stay. */
const isUploadedIcon = (icon: string) =>
    icon.startsWith("http") || icon.startsWith("/");

const lomdaV3StateCreator: StateCreator<LomdaV3EditorState> = (set, get) => ({
    userId: "",
    lomdaId: "",
    lomdaName: "",
    content: { pages: [] },
    metadata: { numOfPages: 0, language: "he", thumbnailUrl: "" },
    assets: [],
    mode: "edit",
    selectedPageId: null,
    selectedBlockId: null,
    revealBlockId: null,
    openSlots: {},
    activeSlot: null,
    clipboardBlock: null,
    editingBlockId: null,
    activeEditor: null,
    activeBlockId: null,
    activeGroupKey: null,
    focusedQuestionId: null,
    htmlPreview: {},
    isSaving: false,
    saveError: null,
    lastSaved: null,
    initialized: false,

    initialize: (data) =>
        set(() => {
            // Always have at least one page — a blank (non-AI) lomda is created
            // with empty content, so seed a default page on open.
            const pages =
                data.content.pages.length > 0
                    ? data.content.pages
                    : [createEmptyPage(0)];
            const content =
                pages === data.content.pages
                    ? data.content
                    : { ...data.content, pages };
            const { assets = [], ...metadata } = data.metadata;
            return {
                userId: data.userId,
                lomdaId: data.lomdaId,
                lomdaName: data.lomdaName,
                content,
                metadata,
                assets,
                mode: "edit" as const,
                selectedPageId: pages[0]?.id ?? null,
                selectedBlockId: null,
                openSlots: {},
                activeSlot: null,
                activeEditor: null,
                isSaving: false,
                saveError: null,
                lastSaved: null,
                initialized: true,
            };
        }),

    setMode: (mode) =>
        set({ mode, activeEditor: null, editingBlockId: null, revealBlockId: null }),
    setSelectedPageId: (pageId) =>
        set({
            selectedPageId: pageId,
            selectedBlockId: null,
            revealBlockId: null,
            activeEditor: null,
        }),
    setSelectedBlockId: (blockId) =>
        set((state) =>
            state.selectedBlockId === blockId
                ? state
                : {
                      selectedBlockId: blockId,
                      activeEditor: null,
                      activeGroupKey: null,
                      focusedQuestionId: null,
                  }
        ),
    revealBlock: (blockId, pageId) =>
        set((state) => {
            const page = state.content.pages.find(
                (p) => p.id === (pageId ?? state.selectedPageId)
            );
            return {
                selectedPageId: page?.id ?? state.selectedPageId,
                ...revealState(state, page?.blocks ?? [], blockId),
            };
        }),
    clearReveal: () => set({ revealBlockId: null }),
    setOpenSlot: (containerId, slotId) =>
        set((state) =>
            state.openSlots[containerId] === slotId
                ? state
                : { openSlots: { ...state.openSlots, [containerId]: slotId } }
        ),
    setActiveSlot: (slot) =>
        set((state) =>
            sameBlockList(state.activeSlot, slot) ? state : { activeSlot: slot }
        ),

    showBlockSettings: (blockId) =>
        set({
            selectedBlockId: blockId,
            activeEditor: null,
            activeGroupKey: null,
            focusedQuestionId: null,
        }),

    setFocusedQuestion: (questionId) =>
        set({ focusedQuestionId: questionId }),

    setHtmlPreview: (blockId, preview) =>
        set((state) => ({
            htmlPreview: { ...state.htmlPreview, [blockId]: preview },
        })),

    openBlockEditor: (blockId) =>
        set({ editingBlockId: blockId, selectedBlockId: blockId }),
    closeBlockEditor: () => set({ editingBlockId: null }),

    setActiveEditor: (editor, blockId, groupKey) =>
        set({
            activeEditor: editor,
            activeBlockId: blockId ?? null,
            activeGroupKey: groupKey ?? null,
        }),
    releaseActiveEditor: (editor) =>
        set((state) =>
            state.activeEditor === editor
                ? { activeEditor: null, activeBlockId: null, activeGroupKey: null }
                : state
        ),
    clearActiveEditor: () =>
        set((state) =>
            state.activeEditor
                ? { activeEditor: null, activeBlockId: null, activeGroupKey: null }
                : state
        ),

    addPage: () =>
        set((state) => {
            const page = createEmptyPage(state.content.pages.length);
            const pages = [...state.content.pages, page];
            return {
                ...withPageMeta(state.content, state.metadata, pages),
                selectedPageId: page.id,
            };
        }),

    deletePage: (pageId) =>
        set((state) => {
            // Never delete the last page — a lomda must keep ≥1 page.
            if (state.content.pages.length <= 1) return state;
            const pages = state.content.pages
                .filter((p) => p.id !== pageId)
                .map((p, i) => ({ ...p, order: i }));
            const selectedPageId =
                state.selectedPageId === pageId
                    ? (pages[0]?.id ?? null)
                    : state.selectedPageId;
            const removed = state.content.pages.find((p) => p.id === pageId);
            const next = withPageMeta(state.content, state.metadata, pages);
            return {
                ...next,
                content: removed
                    ? withoutGoneSettings(next.content, treeIds(removed.blocks))
                    : next.content,
                selectedPageId,
            };
        }),

    duplicatePage: (pageId) =>
        set((state) => {
            const index = state.content.pages.findIndex((p) => p.id === pageId);
            if (index === -1) return state;
            const source = state.content.pages[index];
            const copies = cloneWithSettings(blockSettings(state.content));
            const copy: LomdaV3Page = {
                ...source,
                id: uuidv4(),
                blocks: source.blocks.map(copies.clone),
            };
            const pages = [
                ...state.content.pages.slice(0, index + 1),
                copy,
                ...state.content.pages.slice(index + 1),
            ].map((p, i) => ({ ...p, order: i }));
            const next = withPageMeta(state.content, state.metadata, pages);
            return {
                ...next,
                content: copies.withSettings(next.content),
                selectedPageId: copy.id,
            };
        }),

    reorderPages: (fromIndex, toIndex) =>
        set((state) => {
            if (
                fromIndex === toIndex ||
                fromIndex < 0 ||
                toIndex < 0 ||
                fromIndex >= state.content.pages.length ||
                toIndex >= state.content.pages.length
            ) {
                return state;
            }
            const pages = [...state.content.pages];
            const [moved] = pages.splice(fromIndex, 1);
            pages.splice(toIndex, 0, moved);
            const reindexed = pages.map((p, i) => ({ ...p, order: i }));
            return { content: { ...state.content, pages: reindexed } };
        }),

    setPageTitle: (pageId, title) =>
        set((state) => ({
            content: {
                ...state.content,
                pages: state.content.pages.map((p) =>
                    p.id === pageId ? { ...p, title } : p
                ),
            },
        })),

    setPageBackground: (pageId, background) =>
        set((state) => {
            const page = state.content.pages.find((p) => p.id === pageId);
            if (
                !page ||
                (page.background?.color === background?.color &&
                    page.background?.opacity === background?.opacity)
            ) {
                return state;
            }
            const next: LomdaV3Page = { ...page, background };
            if (!background) delete next.background;
            return {
                content: {
                    ...state.content,
                    pages: state.content.pages.map((p) =>
                        p.id === pageId ? next : p
                    ),
                },
            };
        }),

    clearPageBackgrounds: (pageIds) =>
        set((state) => {
            const ids = new Set(pageIds);
            if (!state.content.pages.some((p) => ids.has(p.id) && p.background))
                return state;
            return {
                content: {
                    ...state.content,
                    pages: state.content.pages.map((p) => {
                        if (!ids.has(p.id) || !p.background) return p;
                        const next = { ...p };
                        delete next.background;
                        return next;
                    }),
                },
            };
        }),

    getSelectedPage: () => {
        const { content, selectedPageId } = get();
        return content.pages.find((p) => p.id === selectedPageId) ?? null;
    },

    addBlock: (block, atIndex) =>
        set((state) => {
            if (!state.selectedPageId) return state;
            const content = withSelectedPageBlocks(
                state.content,
                state.selectedPageId,
                (blocks) => insertBlock(blocks, null, atIndex, block)
            );
            return { content, ...revealNewBlock(state, content, block.id) };
        }),

    addBlockAtInsertPoint: (block) =>
        set((state) => {
            const page = state.content.pages.find(
                (p) => p.id === state.selectedPageId
            );
            if (!page) return state;
            const at = insertPoint(page.blocks, state, block.type);
            const blocks = insertBlock(page.blocks, at.list, at.index, block);
            return {
                content: withSelectedPageBlocks(
                    state.content,
                    state.selectedPageId,
                    () => blocks
                ),
                ...revealState(state, blocks, block.id),
            };
        }),

    updateBlock: (blockId, patch) =>
        set((state) => {
            const content = withBlockPage(state, blockId, (blocks) =>
                mapBlockTree(blocks, (b) =>
                    b.id === blockId
                        ? ({ ...b, data: { ...b.data, ...patch } } as LomdaV3Block)
                        : b
                )
            );
            return content
                ? { content: withoutDroppedSettings(state, content, blockId) }
                : state;
        }),

    patchBlockData: (blockId, type, patch) =>
        set((state) => {
            const block = findBlockDeep(
                state.content.pages.flatMap((p) => p.blocks),
                blockId
            );
            const changes =
                block?.type === type
                    ? patch(block.data as Parameters<typeof patch>[0])
                    : null;
            if (!changes) return state;
            const content = withBlockPage(state, blockId, (blocks) =>
                mapBlockTree(blocks, (b) =>
                    b.id === blockId
                        ? ({ ...b, data: { ...b.data, ...changes } } as LomdaV3Block)
                        : b
                )
            );
            return content
                ? { content: withoutDroppedSettings(state, content, blockId) }
                : state;
        }),

    updateMediaCell: (blockId, index, patch) =>
        get().patchBlockData(blockId, "media-layout", (data) => ({
            cells: data.cells.map((c, i) =>
                i === index
                    ? { ...c, ...(typeof patch === "function" ? patch(c) : patch) }
                    : c
            ),
        })),

    replaceBlockData: (blockId, data) =>
        set((state) => {
            const content = withBlockPage(state, blockId, (blocks) =>
                mapBlockTree(blocks, (b) =>
                    b.id === blockId ? ({ ...b, data } as LomdaV3Block) : b
                )
            );
            return content
                ? { content: withoutDroppedSettings(state, content, blockId) }
                : state;
        }),

    patchBlockBackground: (blockId, patch) =>
        set((state) => {
            const content = withBlockPage(state, blockId, (blocks) =>
                mapBlockTree(blocks, (b) =>
                    b.id === blockId && b.type === "text"
                        ? {
                              ...b,
                              background: patch && { ...b.background, ...patch },
                          }
                        : b
                )
            );
            return content ? { content } : state;
        }),

    changeTextLayout: (blockId, variant) =>
        set((state) => ({
            content: withSelectedPageBlocks(
                state.content,
                state.selectedPageId,
                (blocks) =>
                    mapBlockTree(blocks, (b) =>
                        b.id === blockId && b.type === "text"
                            ? {
                                  ...b,
                                  data: convertTextData(b.data, variant),
                              }
                            : b
                    )
            ),
        })),

    deleteBlock: (blockId) =>
        set((state) => {
            const page = state.content.pages.find(
                (p) => p.id === state.selectedPageId
            );
            const found = findBlockDeep(page?.blocks ?? [], blockId);
            const content = withSelectedPageBlocks(
                state.content,
                state.selectedPageId,
                (blocks) => removeBlockTree(blocks, blockId)
            );
            return {
                content: found
                    ? withoutGoneSettings(content, treeIds([found]))
                    : content,
                selectedBlockId:
                    state.selectedBlockId === blockId
                        ? null
                        : state.selectedBlockId,
            };
        }),

    moveBlockToPage: (blockId, targetPageId) => {
        let moved = false;
        set((state) => {
            if (
                !state.selectedPageId ||
                targetPageId === state.selectedPageId ||
                !state.content.pages.some((p) => p.id === targetPageId)
            )
                return state;
            const src = state.content.pages.find(
                (p) => p.id === state.selectedPageId
            );
            const block = src && findBlockDeep(src.blocks, blockId);
            if (!block) return state;
            moved = true;
            return {
                content: {
                    ...state.content,
                    pages: state.content.pages.map((p) => {
                        if (p.id === state.selectedPageId)
                            return {
                                ...p,
                                blocks: removeBlockTree(p.blocks, blockId),
                            };
                        if (p.id === targetPageId)
                            return { ...p, blocks: [...p.blocks, block] };
                        return p;
                    }),
                },
                selectedBlockId: null,
            };
        });
        return moved;
    },

    copyBlockToPage: (blockId, targetPageId) => {
        let newId: string | null = null;
        set((state) => {
            if (targetPageId === state.selectedPageId) return state;
            const src = state.content.pages.find(
                (p) => p.id === state.selectedPageId
            );
            const block = src && findBlockDeep(src.blocks, blockId);
            if (
                !block ||
                !state.content.pages.some((p) => p.id === targetPageId)
            )
                return state;
            const copies = cloneWithSettings(blockSettings(state.content));
            const copy = copies.clone(block);
            newId = copy.id;
            return {
                content: copies.withSettings({
                    ...state.content,
                    pages: state.content.pages.map((p) =>
                        p.id === targetPageId
                            ? { ...p, blocks: [...p.blocks, copy] }
                            : p
                    ),
                }),
            };
        });
        return newId;
    },

    duplicateBlock: (blockId) =>
        set((state) => {
            let newId: string | null = null;
            const copies = cloneWithSettings(blockSettings(state.content));
            const content = withSelectedPageBlocks(
                state.content,
                state.selectedPageId,
                (blocks) => {
                    const res = duplicateInTree(blocks, blockId, copies.clone);
                    newId = res.newId;
                    return res.blocks;
                }
            );
            if (!newId) return { content };
            const copied = copies.withSettings(content);
            return { content: copied, ...revealNewBlock(state, copied, newId) };
        }),

    copyBlock: (blockId) =>
        set((state) => {
            const page = state.content.pages.find(
                (p) => p.id === state.selectedPageId
            );
            if (!page) return state;
            const found = findBlockDeep(page.blocks, blockId);
            return found
                ? {
                      clipboardBlock: {
                          block: found,
                          settings: blockSettings(state.content),
                      },
                  }
                : state;
        }),

    pasteBlock: () =>
        set((state) => {
            if (!state.clipboardBlock || !state.selectedPageId) return state;
            const copies = cloneWithSettings(state.clipboardBlock.settings);
            const clone = copies.clone(state.clipboardBlock.block);
            const content = withSelectedPageBlocks(
                state.content,
                state.selectedPageId,
                (blocks) => {
                    // Drop it right after the selected block, else at the end.
                    const idx = state.selectedBlockId
                        ? blocks.findIndex((b) => b.id === state.selectedBlockId)
                        : -1;
                    const next = [...blocks];
                    next.splice(idx >= 0 ? idx + 1 : next.length, 0, clone);
                    return next;
                }
            );
            const next = copies.withSettings(content);
            return { content: next, ...revealNewBlock(state, next, clone.id) };
        }),

    addBlockToSlot: (containerId, slotId, block, atIndex) =>
        set((state) => {
            const content = withSelectedPageBlocks(
                state.content,
                state.selectedPageId,
                (blocks) =>
                    insertBlock(blocks, { containerId, slotId }, atIndex, block)
            );
            return { content, ...revealNewBlock(state, content, block.id) };
        }),

    moveBlock: (blockId, { containerId, slotId, index }) =>
        set((state) => {
            const page = state.content.pages.find(
                (p) => p.id === state.selectedPageId
            );
            const from = page && locateBlock(page.blocks, blockId);
            if (!page || !from) return state;
            const to: BlockList =
                containerId && slotId ? { containerId, slotId } : null;
            if (!canMoveBlock(page.blocks, from.block, to)) return state;
            const sameList = sameBlockList(from.list, to);
            // Taking the block out first shifts every later spot up by one.
            const at = sameList && from.index < index ? index - 1 : index;
            if (sameList && at === from.index) return state;
            const blocks = insertBlock(
                removeBlockTree(page.blocks, blockId),
                to,
                at,
                from.block
            );
            return {
                content: withSelectedPageBlocks(
                    state.content,
                    state.selectedPageId,
                    () => blocks
                ),
                selectedBlockId: blockId,
            };
        }),

    setLomdaName: (name) => set({ lomdaName: name }),

    setLanguage: (language) =>
        set((state) => ({
            metadata: { ...state.metadata, language },
        })),

    setPlayerSetting: (key, on) =>
        set((state) => {
            if (!!state.metadata.player?.[key] === on) return state;
            const { [key]: _old, ...rest } = state.metadata.player ?? {};
            const player = on ? { ...rest, [key]: true } : rest;
            return {
                metadata: {
                    ...state.metadata,
                    player: Object.keys(player).length ? player : undefined,
                },
            };
        }),

    setTheme: (theme) =>
        set((state) => ({ metadata: { ...state.metadata, theme } })),

    resetTheme: (brandIcons, switchOwn) =>
        set((state) => {
            // Icons are assets, not styling. With the kit's list at hand the
            // lomda goes back to exactly it (deleted kit icons return, uploads
            // go); without it — a default kit, or the kit failed to load — we
            // can still drop this lomda's own uploads.
            const icons =
                brandIcons ??
                (state.metadata.theme?.customIcons ?? []).filter(
                    (i) => !isLomdaUpload(i)
                );
            const kept = new Set(icons);
            const content = switchOwn
                ? clearOwnFonts(state.content, switchOwn)
                : state.content;
            return {
                metadata: {
                    ...state.metadata,
                    theme: { ...DEFAULT_THEME, customIcons: icons },
                    fontWeights: undefined,
                },
                content: {
                    ...content,
                    blockGap: DEFAULT_BLOCK_GAP,
                    gridGap: DEFAULT_GRID_GAP,
                    innerGap: DEFAULT_INNER_GAP,
                    blockGridGaps: undefined,
                    // Icons dropped by the reset must leave the blocks too.
                    pages: content.pages.map((p) => ({
                        ...p,
                        blocks: clearIcons(
                            p.blocks,
                            (i) => isUploadedIcon(i) && !kept.has(i)
                        ),
                    })),
                },
            };
        }),

    setThemeVar: (key, value) =>
        set((state) => ({
            metadata: {
                ...state.metadata,
                theme: {
                    ...DEFAULT_THEME,
                    ...state.metadata.theme,
                    [key]: value,
                },
            },
        })),

    setThemeFont: (key, family, switchOwn) =>
        set((state) => ({
            metadata: {
                ...state.metadata,
                theme: {
                    ...DEFAULT_THEME,
                    ...state.metadata.theme,
                    [key]: family,
                },
            },
            content: switchOwn
                ? clearOwnFonts(state.content, switchOwn)
                : state.content,
        })),

    addCustomFont: (font) =>
        set((state) => {
            const existing = state.metadata.style?.customFonts ?? [];
            if (existing.some((f) => f.url === font.url)) return state;
            return {
                metadata: {
                    ...state.metadata,
                    style: {
                        ...state.metadata.style,
                        customFonts: [...existing, font],
                    },
                },
            };
        }),

    addCustomIcons: (urls) => {
        const { metadata } = get();
        const theme = metadata.theme ?? DEFAULT_THEME;
        const existing = theme.customIcons ?? [];
        const result = mergeUnique(existing, urls, MAX_CUSTOM_ICONS);
        if (result.added.length)
            set({
                metadata: {
                    ...metadata,
                    theme: {
                        ...theme,
                        customIcons: [...existing, ...result.added],
                    },
                },
            });
        return result;
    },

    deleteMedia: (file) =>
        set((state) => {
            const { theme, deletedAssets } = state.metadata;
            const record = file.url
                ? state.assets.find((a) => a.url === file.url)
                : undefined;
            const clear = (blocks: LomdaV3Block[]) =>
                mapBlockTree(blocks, (b) => withoutMedia(b, file));
            return {
                content: {
                    ...state.content,
                    pages: state.content.pages.map((p) => ({
                        ...p,
                        blocks: clear(p.blocks),
                    })),
                },
                clipboardBlock: state.clipboardBlock
                    ? {
                          ...state.clipboardBlock,
                          block: clear([state.clipboardBlock.block])[0],
                      }
                    : null,
                // The canvas Ctrl+Z is off while a text editor is active.
                activeEditor: null,
                activeBlockId: null,
                activeGroupKey: null,
                metadata: {
                    ...state.metadata,
                    theme: theme?.customIcons?.includes(file.url)
                        ? {
                              ...theme,
                              customIcons: theme.customIcons.filter(
                                  (i) => i !== file.url
                              ),
                          }
                        : theme,
                    deletedAssets: record
                        ? { ...deletedAssets, [record.url]: record.uploadedAt }
                        : deletedAssets,
                },
            };
        }),

    recordAsset: (asset) => {
        const { assets, metadata } = get();
        const i = assets.findIndex((a) => a.url === asset.url);
        // A deleted file uploaded again comes back as a new record.
        const deleted =
            i !== -1 &&
            metadata.deletedAssets?.[asset.url] === assets[i].uploadedAt;
        if (i !== -1 && !deleted) return false;
        set({
            assets: deleted
                ? assets.map((a, j) => (j === i ? asset : a))
                : [...assets, asset],
        });
        return true;
    },

    setBlockTheme: (blockId, override) =>
        set((state) => ({
            content: {
                ...state.content,
                blockThemes: {
                    ...state.content.blockThemes,
                    [blockId]: override,
                },
            },
        })),

    clearBlockTheme: (blockId) =>
        set((state) => {
            const next = { ...state.content.blockThemes };
            delete next[blockId];
            return { content: { ...state.content, blockThemes: next } };
        }),

    setBlockGap: (blockGap) =>
        set((state) => ({
            content: { ...state.content, blockGap },
        })),

    setFontWeight: (preset, weight) =>
        set((state) => ({
            metadata: {
                ...state.metadata,
                fontWeights: {
                    ...state.metadata.fontWeights,
                    [preset]: weight,
                },
            },
        })),

    setLineHeight: (preset, lineHeight) =>
        set((state) => ({
            metadata: {
                ...state.metadata,
                theme: {
                    ...DEFAULT_THEME,
                    ...state.metadata.theme,
                    lineHeights: {
                        ...state.metadata.theme?.lineHeights,
                        [preset]: lineHeight,
                    },
                },
            },
        })),

    setGridGap: (gridGap) =>
        set((state) => ({
            content: { ...state.content, gridGap },
        })),

    setInnerGap: (innerGap) =>
        set((state) => ({
            content: { ...state.content, innerGap },
        })),

    setBlockGridGap: (blockId, gap) =>
        set((state) => ({
            content: {
                ...state.content,
                blockGridGaps: {
                    ...state.content.blockGridGaps,
                    [blockId]: gap,
                },
            },
        })),

    setBlockWidth: (blockId, width) =>
        set((state) => ({
            content: {
                ...state.content,
                blockWidths: {
                    ...state.content.blockWidths,
                    [blockId]: width,
                },
            },
        })),

    setSaveStatus: (status) =>
        set((state) => ({
            isSaving: status.isSaving ?? state.isSaving,
            saveError:
                status.saveError !== undefined
                    ? status.saveError
                    : state.saveError,
            lastSaved: status.lastSaved ?? state.lastSaved,
        })),
});

/**
 * Undo/redo via zundo's temporal middleware. Only the document state (content,
 * metadata, selected page) is tracked, so undo reverts edits, theme changes,
 * page CRUD and page navigation — but not transient UI (selection, mode, save
 * status). A leading throttle coalesces rapid bursts into ~one step.
 */
const undoTracked = (state: Partial<LomdaV3EditorState>) => ({
    content: state.content,
    metadata: state.metadata,
    selectedPageId: state.selectedPageId,
});

// The store updates immutably, so reference checks suffice.
const sameUndoState = (
    a: Partial<LomdaV3EditorState>,
    b: Partial<LomdaV3EditorState>
) =>
    a.content === b.content &&
    a.metadata === b.metadata &&
    a.selectedPageId === b.selectedPageId;

/** The editor's singleton store (carries the undo/redo history). */
const globalStore = create<LomdaV3EditorState>()(
    temporal(lomdaV3StateCreator, {
        // High cap so a full editing session can be undone back to the loaded
        // state. Snapshots share unchanged structure (immutable updates), so the
        // memory cost is ~the deltas, not full copies.
        limit: 1000,
        partialize: undoTracked,
        // Record only when the tracked document state actually changes.
        // Without this, transient UI sets (selection, mode, hover) push no-op
        // snapshots, so pressing undo just pops an identical state and "does
        // nothing".
        equality: sameUndoState,
        handleSet: (handleSet) => burstCollapse(handleSet, 150),
    })
);

export type UndoTracked = ReturnType<typeof undoTracked>;

export type UndoGroup = {
    end: () => void;
    /** Writes of the group that `isOwn` would not recognise (Switch). */
    own: (fn: () => void) => void;
    cancel: (
        revert: (state: LomdaV3EditorState) => Partial<LomdaV3EditorState>
    ) => void;
    /** Stop the group; the history stays as recorded. */
    drop: () => void;
};

/**
 * Everything from now until `end()` becomes ONE undo step, also when it
 * outlasts `burstCollapse` (a slow drag, a colour picked live). With `isOwn`,
 * a write it rejects (an upload finishing meanwhile) keeps its own step: `end()`
 * then leaves the history as recorded, and `cancel(revert)` undoes only the
 * group's change, as one more step. Only a group with nothing else written
 * meanwhile is cancelled exactly, history included.
 */
export function startUndoGroup(
    isOwn?: (prev: UndoTracked, next: UndoTracked) => boolean
): UndoGroup {
    const history = globalStore.temporal;
    const before = undoTracked(globalStore.getState());
    const { pastStates, futureStates } = history.getState();
    const anchor = pastStates.at(-1);
    let foreign = false;
    let owning = false;
    const unwatch = isOwn
        ? globalStore.subscribe((state, prevState) => {
              const prev = undoTracked(prevState);
              const next = undoTracked(state);
              if (!owning && !sameUndoState(prev, next) && !isOwn(prev, next))
                  foreign = true;
          })
        : () => {};
    const recordedSince = () => {
        const past = history.getState().pastStates;
        const at = anchor ? past.lastIndexOf(anchor) : -1;
        return anchor && at < 0
            ? null
            : { kept: past.slice(0, at + 1), recorded: past.slice(at + 1) };
    };
    return {
        end: () => {
            unwatch();
            const since = recordedSince();
            if (
                foreign ||
                !since ||
                sameUndoState(before, undoTracked(globalStore.getState())) ||
                (since.recorded.length === 1 &&
                    sameUndoState(since.recorded[0], before))
            ) {
                return;
            }
            history.setState({
                pastStates: [...since.kept, before],
                futureStates: [],
            });
        },
        own: (fn) => {
            owning = true;
            try {
                fn();
            } finally {
                owning = false;
            }
        },
        cancel: (revert) => {
            unwatch();
            const since = recordedSince();
            if (!isOwn || foreign || !since) {
                globalStore.setState(revert(globalStore.getState()));
                return;
            }
            history.getState().pause();
            globalStore.setState(before);
            history.getState().resume();
            history.setState({ pastStates: since.kept, futureStates });
        },
        drop: unwatch,
    };
}

/** `fn`'s store changes as one undo step. */
export function inOneUndoStep(fn: () => void) {
    const group = startUndoGroup();
    fn();
    group.end();
}

/**
 * Optional store override (defaults to the singleton). Thumbnails render an
 * isolated lomda by supplying their own store via this context, so the same
 * block components can render any lomda read-only without disturbing the editor.
 */
export const LomdaV3StoreContext =
    createContext<StoreApi<LomdaV3EditorState> | null>(null);

/** A fresh, isolated store (no undo history) for rendering a lomda read-only. */
export function createLomdaV3Store(): StoreApi<LomdaV3EditorState> {
    return createStore<LomdaV3EditorState>()(lomdaV3StateCreator);
}

function useLomdaV3EditorBase<T>(selector: (state: LomdaV3EditorState) => T): T {
    const override = useContext(LomdaV3StoreContext);
    return useStore(
        (override ?? globalStore) as StoreApi<LomdaV3EditorState>,
        selector
    );
}

/**
 * Editor store hook. Reads the override store when inside a LomdaV3StoreContext
 * (thumbnails), else the singleton. Static `.getState` / `.temporal` / etc.
 * always target the singleton.
 */
export const useLomdaV3Editor = Object.assign(useLomdaV3EditorBase, {
    getState: globalStore.getState,
    setState: globalStore.setState,
    subscribe: globalStore.subscribe,
    temporal: globalStore.temporal,
});
